import React, { useState, useMemo } from 'react'
import take from 'lodash/take'
import slice from 'lodash/slice'
import values from 'lodash/values'
import chunk from 'lodash/chunk'
import { queryClient } from '~/common/react-query/queryClient'
import {
  asLink,
  Skeleton,
  Badge,
  Box,
  Card,
  CardHeader,
  CardHeading,
  CardActionLink,
  CardBody,
  CardFooter,
  Link,
  TextBlockInline,
  Text,
  HStack,
  VStack,
  Stack,
  useMediaQuery,
  EmptyState
} from '~/design_system'
import { useMsg, Msg } from '~/common/localizationUtils'
import { SadStates } from '~/components/SadStates'
import { CountPager } from '~/components/v2/CountPager'
import { useFetchTaskSummaryQuery, USER_TASK_SUMMARY_QUERY_KEY } from '~/api/myTasks'
import { CATEGORIES_ICON_MAP } from './constants'
import { UserTaskCategory, UserTaskStatus } from './types'

const MAX_DESKTOP_CATEGORIES_PER_PAGE = 14
const MAX_DESKTOP_CATEGORIES_PER_COLUMN = 7
const MAX_MOBILE_CATEGORIES_PER_PAGE = 5
const MAX_NUM_CATEGORIES_IN_COLUMN_BEFORE_WIDGET_EXTENDS = 5
const taskListHref = `/tasks/list?status=${UserTaskStatus.Pending},${UserTaskStatus.Overdue}`

export const HomeTaskWidget = () => {
  const msg = useMsg()
  const { data, isLoading, isError }: any = useFetchTaskSummaryQuery()

  const [isMobile] = useMediaQuery('(max-width: 900px)')

  const taskSummaryResults = data?.results ?? {}
  const categoriesData = useMemo(() => {
    return values(taskSummaryResults)
      .filter((category: UserTaskCategory) => category.pending_count + category.overdue_count > 0)
      .map((category: UserTaskCategory) => ({
        ...category,
        icon: CATEGORIES_ICON_MAP[category.key]
      }))
  }, [taskSummaryResults])

  const [pageMobile, setPageMobile] = useState<number>(0)
  const [pageDesktop, setPageDesktop] = useState<number>(0)
  const totalPagesMobile = chunk(categoriesData, MAX_MOBILE_CATEGORIES_PER_PAGE).length
  const totalPagesDesktop = chunk(categoriesData, MAX_DESKTOP_CATEGORIES_PER_PAGE).length

  return (
    <Card>
      <CardHeader>
        <CardHeading>{msg('tasks.card.header')}</CardHeading>
        <CardActionLink href={taskListHref}>{msg('tasks.card.view_all.link')}</CardActionLink>
      </CardHeader>
      <CardBody p="0">
        <SadStates
          states={[
            {
              when: !isLoading && isError,
              render: () => (
                <EmptyState>
                  <EmptyState.RefreshError
                    title={<Msg id="common.ui.content_failed_to_load" />}
                    error={new Error('testing')}
                    onRefresh={() => queryClient.invalidateQueries([USER_TASK_SUMMARY_QUERY_KEY])}
                  />
                </EmptyState>
              )
            },
            {
              when: !isLoading && categoriesData.length === 0,
              render: () => (
                <EmptyState>
                  <EmptyState.CaughtUp />
                  <TaskListLink />
                </EmptyState>
              )
            }
          ]}>
          {isMobile ? (
            <HomeTaskWidgetMobile categoriesData={categoriesData} page={pageMobile} isLoaded={!isLoading} />
          ) : (
            <HomeTaskWidgetDesktop categoriesData={categoriesData} page={pageDesktop} isLoaded={!isLoading} />
          )}
        </SadStates>
      </CardBody>
      {isMobile && categoriesData.length > MAX_MOBILE_CATEGORIES_PER_PAGE && (
        <CardFooter justifyContent="center">
          <CountPager
            page={pageMobile + 1}
            totalPages={totalPagesMobile}
            totalResults={categoriesData.length}
            onPrevPage={() => turnPage(setPageMobile, totalPagesMobile)(false)}
            onNextPage={() => turnPage(setPageMobile, totalPagesMobile)(true)}
          />
        </CardFooter>
      )}
      {!isMobile && categoriesData.length > MAX_DESKTOP_CATEGORIES_PER_PAGE && (
        <CardFooter justifyContent="center">
          <CountPager
            page={pageDesktop + 1}
            totalPages={totalPagesDesktop}
            totalResults={categoriesData.length}
            onPrevPage={() => turnPage(setPageDesktop, totalPagesDesktop)(false)}
            onNextPage={() => turnPage(setPageDesktop, totalPagesDesktop)(true)}
          />
        </CardFooter>
      )}
    </Card>
  )
}

const HomeTaskWidgetMobile = ({
  categoriesData = [],
  page = 0,
  isLoaded = false
}: {
  categoriesData: UserTaskCategory[]
  page: number
  isLoaded: boolean
}) => {
  const categoryListPages = chunk(categoriesData, MAX_MOBILE_CATEGORIES_PER_PAGE)
  const renderPage = categoryListPages[page]
  return (
    <VStack w="fill-available" spacing={0} minHeight="285px" alignItems="normal">
      {!isLoaded && <TaskWidgetSkeleton />}
      {isLoaded &&
        renderPage?.map((category: UserTaskCategory, index: number) => (
          <CategoryListItem
            key={category.key}
            category={category}
            includeBottomBorder={index !== renderPage.length - 1 || renderPage.length < MAX_MOBILE_CATEGORIES_PER_PAGE}
          />
        ))}
    </VStack>
  )
}

const HomeTaskWidgetDesktop = ({
  categoriesData = [],
  page = 0,
  isLoaded = false
}: {
  categoriesData: UserTaskCategory[]
  page: number
  isLoaded: boolean
}) => {
  const [col1Categories, col2Categories] = useMemo(() => {
    const categoryPages = chunk(categoriesData, MAX_DESKTOP_CATEGORIES_PER_PAGE)
    const categoryPage = categoryPages[page]
    const col1: UserTaskCategory[] = take(categoryPage, MAX_DESKTOP_CATEGORIES_PER_COLUMN)
    const col2: UserTaskCategory[] = slice(
      categoryPage,
      MAX_DESKTOP_CATEGORIES_PER_COLUMN,
      MAX_DESKTOP_CATEGORIES_PER_PAGE
    )
    return [col1, col2]
  }, [page, categoriesData])

  return (
    <HStack justify="space-between" align="flex-start" spacing={0} minHeight="285px">
      <Stack
        w="fill-available"
        spacing={0}
        borderRight={col2Categories.length > 0 ? '1px solid' : 'none'}
        borderRightColor="gray.200">
        {!isLoaded && <TaskWidgetSkeleton />}
        {isLoaded &&
          col1Categories.map((category: UserTaskCategory, index: number) => (
            <CategoryListItem
              category={category}
              key={category.key}
              includeBottomBorder={
                index !== col1Categories.length - 1 ||
                col1Categories.length < MAX_NUM_CATEGORIES_IN_COLUMN_BEFORE_WIDGET_EXTENDS
              }
            />
          ))}
      </Stack>
      {isLoaded && col2Categories.length > 0 && (
        <Stack w="fill-available" spacing={0}>
          {col2Categories.map((category: UserTaskCategory, index: number) => (
            <CategoryListItem
              category={category}
              key={category.key}
              includeBottomBorder={
                index !== col2Categories.length - 1 ||
                col2Categories.length < MAX_NUM_CATEGORIES_IN_COLUMN_BEFORE_WIDGET_EXTENDS
              }
            />
          ))}
        </Stack>
      )}
    </HStack>
  )
}
interface CategoryListItemProps {
  category: UserTaskCategory
  includeBottomBorder?: boolean
}
const CategoryListItem = ({ category, includeBottomBorder = false }: CategoryListItemProps) => {
  return (
    <Box
      {...asLink(`${taskListHref}&category_name=${category.key}`)}
      data-testid={`homepage-task-widget-category-div-${category.key}`}
      p={4}
      borderBottomWidth={includeBottomBorder ? '1px' : '0'}
      _hover={{ backgroundColor: 'gray.50', cursor: 'pointer' }}
      sx={{ ':hover a': { textDecoration: 'underline' } }}>
      <TextBlockInline alignItems="center" spacing={3} w="full" justify="space-between">
        <HStack>
          {category.icon && <category.icon w="24px" color="blue.500" />}
          <Text textStyle="bodySemi">{category.label}</Text>
        </HStack>
        <CategoryListItemBadge pendingCount={category.pending_count} overdueCount={category.overdue_count} />
      </TextBlockInline>
    </Box>
  )
}

const TaskListLink = () => {
  const msg = useMsg()

  return (
    <Link variant="link" href={taskListHref} _focus={{ boxShadow: 'none' }} target="_top">
      {msg('tasks.card.view_all.link')}
    </Link>
  )
}

const TaskWidgetSkeleton = () => {
  return (
    <Stack px={4}>
      <Skeleton height="40px" mt={4} />
      <Skeleton height="40px" />
      <Skeleton height="40px" />
      <Skeleton height="40px" />
      <Skeleton height="40px" />
    </Stack>
  )
}

const turnPage = (setPage: React.Dispatch<React.SetStateAction<number>>, totalPages: number) => (forward: boolean) => {
  if (forward) {
    setPage(page => Math.min(page + 1, totalPages - 1))
  } else {
    setPage(page => Math.max(page - 1, 0))
  }
}

const CategoryListItemBadge = ({ pendingCount, overdueCount }: { pendingCount: number; overdueCount: number }) => {
  const msg = useMsg()

  if (overdueCount > 0) {
    return (
      <Badge variant="danger" size="sm">
        {pendingCount + overdueCount} {msg('tasks.badge.tasks')} ({overdueCount} {msg('tasks.badge.overdue')})
      </Badge>
    )
  }
  return (
    <Badge variant="incomplete" size="sm">
      {pendingCount + overdueCount} {msg('tasks.badge.tasks')}
    </Badge>
  )
}
